import { isSuccess, type Result } from '@carvana/merch-result';
import { axios, httpWithAuth, isAxiosError } from '~/clients';
import {
  NBA_AUTH_BFF_FEED_URL,
  NBA_UNAUTH_BFF_FEED_URL
} from '~/constants/home';
import type { BffFeedItem } from '~/contracts/home';
import type { FeedData } from '~/services/home/contracts';
import { logError, toError } from '~/utilities/errorHandlers';

type FeedRequest = {
  isFinanceRestricted?: boolean;
  useAuth: boolean;
};

export type FeedResponse = Result<{
  bcid?: string;
  feedItems: BffFeedItem[];
  userId?: string;
}>;

export default async function fetchFeed(
  { isFinanceRestricted = false, useAuth }: FeedRequest,
  controller: AbortController
): Promise<FeedData> {
  const apiResponse: FeedData = {
    errorEventPayload: {},
    feedItems: [],
    isError: false
  };

  try {
    const fetcher = useAuth ? httpWithAuth : axios;
    const url = useAuth ? NBA_AUTH_BFF_FEED_URL : NBA_UNAUTH_BFF_FEED_URL;

    const res = await fetcher.post<FeedResponse>(
      url,
      { includeFinancePromo: !isFinanceRestricted },
      { signal: controller.signal }
    );

    if (!isSuccess(res.data)) {
      throw new Error(res.data.errorMessage || 'Unknown error');
    }

    apiResponse.feedItems = res.data.content.feedItems;

    return apiResponse;
  } catch (error) {
    apiResponse.isError = true;

    if (isAxiosError(error)) {
      const { code, message, response } = error;

      apiResponse.errorEventPayload = {
        statusCode: code || response?.status,
        errorMessage: message
      };
    } else {
      apiResponse.errorEventPayload = {
        errorMessage: toError(error).message
      };
    }

    logError(error, { location: 'Get next best action' });

    return apiResponse;
  }
}
